import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ShieldCheck, ShieldAlert, ShieldQuestion, Loader2, Upload, CheckCircle2, XCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AddressVerify({ booking, token, onSaved }) {
  const [verifying, setVerifying] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [billVerifying, setBillVerifying] = useState(false);
  const v = booking.address_verification;

  const verify = async () => {
    setVerifying(true);
    try {
      await axios.post(`${API}/bookings/${booking.id}/verify-address`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Address checked against the license.");
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not verify address.");
    } finally {
      setVerifying(false);
    }
  };

  const uploadBillAndVerify = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setUploading(false);
      setBillVerifying(true);
      await axios.post(`${API}/bookings/${booking.id}/verify-bill`, { bill_path: data.path }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Bill checked.");
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not verify the bill.");
    } finally {
      setUploading(false);
      setBillVerifying(false);
    }
  };

  const verified = v?.verified;
  const badge = !v ? { icon: ShieldQuestion, cls: "text-white/50 border-white/15 bg-white/5", label: "Address not checked" }
    : verified ? { icon: ShieldCheck, cls: "text-green-400 border-green-500/40 bg-green-500/10", label: v.status === "verified-by-bill" ? "Verified by bill" : "License matches" }
    : { icon: ShieldAlert, cls: "text-red-400 border-red-500/40 bg-red-500/10", label: v.status === "unreadable" ? "License unreadable" : v.status === "bill-failed" ? "Bill did not verify" : "Address mismatch" };
  const Icon = badge.icon;

  return (
    <div className="mt-3 rounded-lg border border-white/10 bg-[#0A0A0A] p-3" data-testid={`address-verify-${booking.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className={`text-xs px-2.5 py-1 rounded-full border flex items-center gap-1.5 ${badge.cls}`}><Icon size={13} /> {badge.label}</span>
        <button data-testid={`verify-address-btn-${booking.id}`} onClick={verify} disabled={verifying || !booking.license_front_path}
          className="text-xs flex items-center gap-1.5 border border-[#F2B705]/50 text-[#F2B705] px-3 py-1.5 rounded hover:bg-[#F2B705] hover:text-black transition-colors disabled:opacity-40"
          title={!booking.license_front_path ? "No license image on file" : ""}>
          {verifying ? <Loader2 className="animate-spin" size={13} /> : <ShieldCheck size={13} />} {v ? "Re-check" : "Verify Address"}
        </button>
      </div>

      {v && (
        <div className="mt-2 text-xs text-white/55 space-y-0.5">
          <div>System: <span className="text-white/75">{v.claimed_address || "—"}</span></div>
          {v.license_address ? <div>License: <span className="text-white/75">{v.license_address}</span></div> : null}
          {v.bill_address ? <div>Bill: <span className="text-white/75">{v.bill_address}</span> {v.bill_date ? `(${v.bill_date})` : ""} {v.bill_within_90_days === false && <span className="text-red-400">— older than 90 days</span>}</div> : null}
          {v.dob ? (
            <div className="flex items-center gap-2 pt-1" data-testid={`age-info-${booking.id}`}>
              <span>Age: <span className="text-white/75">{v.age != null ? v.age : "—"}</span> <span className="text-white/35">(DOB {v.dob})</span></span>
              {v.under_25 ? (
                <span className="text-xs px-2 py-0.5 rounded-full border border-orange-400/40 bg-orange-400/10 text-orange-300">Under 25 — young-driver fee</span>
              ) : v.age != null ? (
                <span className="text-xs px-2 py-0.5 rounded-full border border-green-500/40 bg-green-500/10 text-green-400">25+</span>
              ) : null}
            </div>
          ) : null}
        </div>
      )}

      {v && !verified && (
        <div className="mt-3 border-t border-white/10 pt-3">
          <p className="text-xs text-white/50 mb-2">Address didn't match — upload a utility bill / statement showing the current address (dated within the last 90 days).</p>
          <label data-testid={`bill-upload-${booking.id}`} className="inline-flex items-center gap-2 cursor-pointer border-2 border-dashed border-white/15 hover:border-[#F2B705]/50 rounded-lg px-4 py-2 text-sm text-white/60 transition-colors">
            {(uploading || billVerifying) ? <Loader2 className="animate-spin" size={15} /> : <Upload size={15} />}
            {uploading ? "Uploading…" : billVerifying ? "Checking bill…" : "Upload & verify bill"}
            <input type="file" accept="image/*" className="hidden" onChange={uploadBillAndVerify} />
          </label>
        </div>
      )}
    </div>
  );
}
